// The buyer enters a supplier's emailed bid file (Lane 1394, BUILD_PLAN A6): download the
// supplier's file to email it, upload the filled file, review what it changes (nothing is
// saved yet), then enter it. The server reads the file itself and checks it again.
import React, { useRef, useState } from 'react';
import { formatUnitPrice, newKey } from './sourcingApi';
import { saveBlob } from './DocumentsTab';

const CHANGE_TEXT = { new: 'New', changed: 'Changed', same: 'Same', not_quoted: 'Not quoted' };

function value(v, currency) {
  if (!v) return '-';
  if (v.no_bid) return 'No bid';
  return v.price === null || v.price === undefined ? '-' : formatUnitPrice(v.price, currency);
}

const num = (v, k) => (v && v[k] !== null && v[k] !== undefined ? Number(v[k]).toLocaleString() : '-');

// "Changed: price, minimum order" (every field the entry replaces; Codex r1 #5).
function changeText(r) {
  if (r.change !== 'changed') return CHANGE_TEXT[r.change] || r.change;
  const f = r.changed_fields || [];
  return f.length ? `${f[0][0].toUpperCase()}${f.join(', ').slice(1)}` : 'Changed';
}

export default function SupplierFileEntry({ api, eventId, supplier, canEnter, onClose, onEntered }) {
  const [file, setFile] = useState(null);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  // One request key per reviewed file, kept across retries: a request that committed
  // but lost its answer replays instead of being refused (Codex r1 #4).
  const enterKey = useRef(null);
  const name = supplier.supplier_name;

  const download = () => api.downloadSupplierFile(eventId, supplier.supplier_id)
    .then((b) => saveBlob(b, `quote-${name.replace(/[^A-Za-z0-9]+/g, '-')}.xlsx`))
    .catch((e) => setError(e.message));
  const choose = async (f) => {
    setFile(f); setReview(null); setError(''); setDone('');
    enterKey.current = newKey();
    if (!f) return;
    setBusy(true);
    try { setReview(await api.reviewSupplierFile(eventId, supplier.supplier_id, f)); } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  };
  const enter = async () => {
    setBusy(true); setError('');
    try {
      const r = await api.enterSupplierFile(eventId, supplier.supplier_id, file, review.file_digest, enterKey.current);
      const res = (r && r.result) || {};
      setDone(`Entered for ${name}: ${res.priced || 0} priced, ${res.no_bid || 0} no bid. The scenarios calculate again on their own.`);
      setReview(null); setFile(null);
      if (onEntered) onEntered();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const cur = review ? review.currency : 'USD';
  return (
    <section className="srce-card2" aria-label={`Enter ${name}'s file`}>
      <div className="srce-card2-h">
        <span className="srce-card2-t">Enter {name}'s bid file</span>
        <span className="srce-push srce-row">
          <button type="button" className="srce-btn" onClick={download}>Download {name}'s bid file</button>
          <button type="button" className="srce-btn" onClick={onClose}>Close</button>
        </span>
      </div>
      <div className="srce-card2-f srce-col srce-gap">
        <div className="srce-mut">Use this event's bid file: download it here and email it to {name}, or use the file they downloaded from the portal. Nothing is saved until you press Enter.</div>
        <label className="srce-row">
          <span>Filled file</span>
          <input type="file" accept=".xlsx" disabled={busy || !canEnter} onChange={(e) => choose(e.target.files && e.target.files[0])} />
        </label>
        {!canEnter && <div className="srce-mut">Offers can be entered only while a round is open.</div>}
        {busy && <div className="srce-mut">Reading the file…</div>}
        {error && <div className="srce-error" role="alert">{error}</div>}
        {done && <div className="srce-good" role="status">{done}</div>}
        {review && (
          <>
            <div>{review.priced} priced, {review.no_bid} no bid{review.errors.length ? `; ${review.errors.length} ${review.errors.length === 1 ? 'row has a problem' : 'rows have problems'}` : ''}.</div>
            {review.kept_note && <div className="srce-mut">{name}'s note on their last quote stays with these offers: "{review.kept_note}"</div>}
            {review.newer_offer && <div className="srce-warn">{name} submitted or withdrew prices after this file was made. Download a new file and ask them to fill it in.</div>}
            {review.errors.length > 0 && (
              <div className="srce-mini srce-mini--errs">
                <div className="srce-mini-h"><span>Row</span><span>Part</span><span>Problem</span></div>
                {review.errors.map((x) => (
                  <div key={`${x.row}-${x.part_number}`} className="srce-mini-r">
                    <span>{x.row}</span><span className="srce-ell" title={x.part_number || ''}>{x.part_number || '-'}</span>
                    <span className="srce-neg srce-ell" title={x.reason}>{x.reason}</span>
                  </div>
                ))}
              </div>
            )}
            {review.rows.length > 0 && (
              <div className="srce-mini srce-mini--entry">
                <div className="srce-mini-h"><span>Part</span><span className="srce-wt-num">Price now</span>
                  <span className="srce-wt-num">Price in file</span><span className="srce-wt-num">Min. order in file</span>
                  <span className="srce-wt-num">Lead time in file</span><span>Change</span></div>
                {review.rows.map((r) => (
                  <div key={r.part_number} className="srce-mini-r"
                    title={`${r.description || r.part_number}${r.current ? `. Now: minimum order ${num(r.current, 'min_order_units')}, lead time ${num(r.current, 'lead_time_days')} days` : ''}${r.file.note ? `. Note in file: ${r.file.note}` : ''}`}>
                    <span className="srce-ell">{r.part_number}</span>
                    <span className="srce-wt-num">{value(r.current, cur)}</span>
                    <span className="srce-wt-num">{value(r.file, cur)}</span>
                    <span className="srce-wt-num">{num(r.file, 'min_order_units')}</span>
                    <span className="srce-wt-num">{r.file.lead_time_days !== null && r.file.lead_time_days !== undefined ? `${r.file.lead_time_days} days` : '-'}</span>
                    <span className="srce-ell">{changeText(r)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="srce-row">
              <button type="button" className="srce-btn srce-btn--primary" disabled={busy || !review.can_enter} onClick={enter}>
                Enter these offers for {name}</button>
              {!review.can_enter && !review.newer_offer && review.errors.length > 0
                && <span className="srce-mut">Fix the rows with problems in the file and upload it again.</span>}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
